<template>
  <transition name="wheelFade">
    <div class="luckyWheelMask" v-if="show" @click.self="close">
      <div class="wheelPanel">
        <i class="closeBt" @click="close">×</i>

        <!-- 氛围光斑 -->
        <span class="glow glowA"></span>
        <span class="glow glowB"></span>

        <div class="wheelTitle">🎡 幸运祝福转盘</div>
        <div class="wheelSub">点击发送 · 随机送出一份祝福</div>

        <div class="wheelWrap" :style="{ width: wheelSize + 'px', height: wheelSize + 'px' }">
          <!-- 顶部指针 -->
          <div class="wheelPointer"></div>
          <!-- 转盘本体：canvas 绘制，整体 CSS 旋转（transition 只在转动时挂载） -->
          <canvas ref="wheelCanvas" class="wheelCanvas" :style="canvasStyle"></canvas>
          <!-- 中心发送按钮 -->
          <button class="spinBt" :disabled="spinning || sending || !segments.length" @click="spin">
            <span v-if="!spinning && !sending">{{ $i18n('send', '发送') }}</span>
            <span v-else class="spinDots">···</span>
          </button>
        </div>

        <div class="wheelResult" v-if="resultText">{{ resultText }}</div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapGetters } from "vuex";
import i18nMixin from "@/libs/i18nMixin";

/* effect 礼物 icon 兜底（与 GiftMenu 一致） */
const EFFECT_ICON_FALLBACK = {
  heart: "❤️", hongbao: "🧧", rose: "🌹", champagne: "🥂", cake: "🎂",
  ring: "💍", wishStar: "🌟", cherryBlossom: "🌸", butterfly: "🦋",
  loveLetter: "💌", hotAirBalloon: "🎈", firework: "🎆", diamondRain: "💎",
  crown: "👑", sportsCar: "🏎️", lemon: "🍋", suona: "🎺", bulb: "💡",
  pigTrotter: "🐷", garlic: "🧄", six666: "🔥",
};

const SPIN_TURNS = 5;           // 每次转 5 圈
const SPIN_MS = 4000;           // 旋转动画时长（与下方 transition 时长一致）
const ROUND_SIZE = 6;           // ★ 每轮上盘的礼物数量
const NEXT_ROUND_DELAY = 500;   // 送达退出后，重载并重新弹出的间隔
const RESULT_STAY_MS = 1000;    // 发送成功结果文案停留时长

export default {
  name: "LuckyGiftWheel",
  mixins: [i18nMixin],
  data() {
    return {
      show: false,
      spinning: false,
      sending: false,
      rotateDeg: 0,        // 转盘旋转角度（每轮重置为 0）
      resultText: "",
      segments: [],        // 本轮上盘的 6 个礼物
      wheelSize: 300,
      _targetIdx: -1,
    };
  },
  computed: {
    ...mapGetters(["live"]),
    /** ★ transition 只在 spinning 时挂载：每轮 reset 到 0 度瞬时完成，不回转 */
    canvasStyle() {
      return {
        transform: "rotate(" + this.rotateDeg + "deg)",
        transition: this.spinning
            ? "transform " + (SPIN_MS / 1000) + "s cubic-bezier(0.12, 0.8, 0.16, 1)"
            : "none",
      };
    },
  },
  watch: {
    /**
     * ★ 祝福墙开关驱动弹窗（状态变了才动）：
     * - isOpenWishLive → true：自动弹出
     * - isOpenWishLive → false：自动收起（转动/发送中则等这轮结束后收）
     */
    "live.isOpenWishLive"(val) {
      if (val) {
        this.autoOpen();
      } else {
        this.closeByState();
      }
    },
  },
  mounted() {
    this.$Bus.$on("openLuckyWheel", this.open); // 推送触发（随时可弹）
    // live 信息已就绪的场景（组件挂载晚于 initLive / keep-alive 恢复）补一次检查
    if (this.live && this.live.isOpenWishLive) {
      this.autoOpen();
    }
  },
  beforeDestroy() {
    this.$Bus.$off("openLuckyWheel", this.open);
    clearTimeout(this._spinTimer);
    clearTimeout(this._resultTimer);
    clearTimeout(this._reopenTimer);
    clearTimeout(this._autoOpenTimer);
  },
  methods: {
    /* ═══════════ 打开 / 关闭 ═══════════ */

    /** ★ isOpenWishLive=true 驱动的自动弹出 */
    autoOpen() {
      if (this.show || this.spinning || this.sending) return;
      clearTimeout(this._autoOpenTimer);
      this._autoOpenTimer = setTimeout(() => this.open(), 600);
    },

    /** ★ 每次打开都是新的一轮：重置角度、清盘、重新加载并随机选 6 个 */
    open() {
      this.wheelSize = Math.min(300, window.innerWidth - 110);
      this.show = true;
      this.resultText = "";
      this.spinning = false;
      this.sending = false;
      this.rotateDeg = 0;   // transition 为 none，瞬时归零不回转
      this.segments = [];

      this.$nextTick(() => {
        // 清掉上一轮的画布残影
        const c = this.$refs.wheelCanvas;
        if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
        this.loadRound();
      });
    },

    /** 用户手动关闭：本轮终止，不再自动重开（发送成功路径除外） */
    close() {
      if (this.spinning || this.sending) return;
      this.show = false;
      clearTimeout(this._reopenTimer);
      clearTimeout(this._resultTimer);
    },

    /** ★ 状态关闭收起：转动/发送中先记一笔，这轮结束后再收 */
    closeByState() {
      clearTimeout(this._reopenTimer);
      clearTimeout(this._resultTimer);
      if (!this.show) return;
      if (this.spinning || this.sending) {
        this._closePending = true;
      } else {
        this.show = false;
      }
    },

    /* ═══════════ 每轮数据：重载列表 → 随机选 6 个 ═══════════ */
    /* ═══════════ 每轮数据：重载列表 → 至少 2 个霸屏 + 普通礼物补足 ═══════════ */
    async loadRound() {
      try {
        const [giftRes, blastRes] = await Promise.all([
          this.$http.get("/gift/getGiftList", { params: { liveId: this.live.id, page: 1, size: 999 } }),
          this.$http.get("/gift/getBlastGiftList", { params: { liveId: this.live.id } }),
        ]);
        const giftList = giftRes.data || [];
        const blastRaw = blastRes.data || [];

        const gifts = giftList.map((g) => ({
          type: "gift",
          id: g.id,
          name: g.giftName,
          icon: g.giftType === "effect" ? (g.icon || EFFECT_ICON_FALLBACK[g.code] || "✨") : "🎁",
          img: g.coverUrl || "",
          blast: false,
        }));
        const blasts = blastRaw
            .filter((b) => b && typeof b === "object")
            .map((b) => ({
              type: "blast",
              code: b.code || b.name,
              name: b.desc || b.name || "霸屏礼物",
              icon: "🎆",
              img: b.icon || "",
              blast: true,
            }));

        if (!gifts.length && !blasts.length) {
          this.$toast("暂无可送的礼物");
          this.show = false;
          return;
        }

        // ★ 分池洗牌：普通礼物、霸屏礼物各自随机
        this.shuffle(gifts);
        this.shuffle(blasts);

        // ★ 先拿 2 个霸屏（不足 2 个有几个拿几个），普通礼物补足 6 个
        let roundBlasts = blasts.slice(0, Math.min(2, blasts.length));
        let roundGifts = gifts.slice(0, ROUND_SIZE - roundBlasts.length);

        // 兜底：普通礼物不够 4 个时，从剩余霸屏补；仍不够则允许霸屏重复，保证 6 格摆满不留空
        if (roundGifts.length + roundBlasts.length < ROUND_SIZE) {
          const need = ROUND_SIZE - roundGifts.length - roundBlasts.length;
          roundBlasts = roundBlasts.concat(blasts.slice(roundBlasts.length, roundBlasts.length + need));
          while (roundGifts.length + roundBlasts.length < ROUND_SIZE && blasts.length) {
            roundBlasts.push(blasts[Math.floor(Math.random() * blasts.length)]);
          }
        }

        // ★ 霸屏均匀交错插入普通礼物之间，视觉分布均匀不扎堆
        this.segments = this.interleave(roundGifts, roundBlasts);

        // 预加载本轮图片，失败用 emoji 兜底
        await Promise.all(this.segments.map(async (s) => {
          s.imgEl = s.img ? await this.loadImage(s.img) : null;
        }));

        this.drawWheel();
      } catch (e) {
        console.error("[LuckyGiftWheel] 本轮加载失败:", e);
        this.$toast.fail("礼物加载失败");
        this.show = false;
      }
    },

    /** ★ 霸屏礼物均匀分布到普通礼物的间隔中（按等分位置落位） */
    interleave(gifts, blasts) {
      const total = gifts.length + blasts.length;
      const segs = new Array(total);
      blasts.forEach((b, i) => {
        segs[Math.floor((i * total) / blasts.length)] = b;
      });
      let gi = 0;
      for (let i = 0; i < total; i++) {
        if (!segs[i]) segs[i] = gifts[gi++];
      }
      return segs;
    },
    shuffle(arr) {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
    },

    /** 加载单张图片，3 秒超时 / 失败返回 null（用 emoji 兜底） */
    loadImage(url) {
      return new Promise((resolve) => {
        if (!url) return resolve(null);
        const img = new Image();
        const timer = setTimeout(() => resolve(null), 3000);
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); resolve(null); };
        img.src = url;
      });
    },

    /* ═══════════ canvas 绘制转盘 ═══════════ */
    /* ═══════════ canvas 绘制转盘 ═══════════ */
    drawWheel() {
      const canvas = this.$refs.wheelCanvas;
      if (!canvas || !this.segments.length) return;

      const size = this.wheelSize;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = size * dpr;
      canvas.height = size * dpr;
      canvas.style.width = size + "px";
      canvas.style.height = size + "px";
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // 防止重复绘制时 scale 累加

      const cx = size / 2, cy = size / 2;
      const r = size / 2 - 8;
      const n = this.segments.length;
      const arc = (Math.PI * 2) / n;

      const nameFs = n <= 8 ? 13 : 11;
      const nameLen = n <= 8 ? 5 : 4;
      const imgSize = Math.max(20, Math.min(40, r * 0.32));

      for (let i = 0; i < n; i++) {
        const seg = this.segments[i];
        const start = i * arc - Math.PI / 2; // 0 号格从顶部指针处开始

        // 扇形底色：普通礼物暖金系 / 霸屏礼物粉紫系
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, r, start, start + arc);
        ctx.closePath();
        ctx.fillStyle = seg.blast
            ? (i % 2 ? "#ffd9e8" : "#ffc2da")
            : (i % 2 ? "#fff6e3" : "#ffe9c4");
        ctx.fill();
        ctx.strokeStyle = "rgba(217,162,74,.8)";
        ctx.lineWidth = 1;
        ctx.stroke();

        // ★ 内容沿扇形中线绘制：
        // rotate(θ) 后画 (0, -d)，落点在角度 (θ - 90°)，
        // 所以这里要 rotate(mid + 90°)，内容才正好落在中线 mid 上
        // （原代码少加了 90°，内容全部压在了分隔线上）
        const mid = start + arc / 2;
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(mid + Math.PI / 2);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        // 名称
        ctx.fillStyle = seg.blast ? "#b3476e" : "#8a5a20";
        ctx.font = `bold ${nameFs}px 'PingFang SC', sans-serif`;
        ctx.fillText(this.short(seg.name, nameLen), 0, -r + 22);

        // 礼物图 / emoji 兜底
        if (seg.imgEl) {
          try {
            ctx.drawImage(seg.imgEl, -imgSize / 2, -r * 0.52 - imgSize / 2, imgSize, imgSize);
          } catch (e) {
            ctx.font = `${Math.round(imgSize * 0.8)}px sans-serif`;
            ctx.fillText(seg.icon, 0, -r * 0.52);
          }
        } else {
          ctx.font = `${Math.round(imgSize * 0.8)}px sans-serif`;
          ctx.fillText(seg.icon, 0, -r * 0.52);
        }
        ctx.restore();
      }

      // 外圈
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.lineWidth = 5;
      ctx.strokeStyle = "#f5b94e";
      ctx.stroke();
    },
    short(s, len) {
      s = s || "";
      return s.length > len ? s.slice(0, len) + "…" : s;
    },

    /* ═══════════ 转动 + 发送 ═══════════ */
    spin() {
      if (this.spinning || this.sending || !this.segments.length) return;
      this.spinning = true; // canvasStyle 立即挂上 transition
      this.resultText = "";

      const n = this.segments.length;
      const arcDeg = 360 / n;
      const idx = Math.floor(Math.random() * n); // ★ 随机目标格
      this._targetIdx = idx;

      const targetCenter = idx * arcDeg + arcDeg / 2;
      const base = ((this.rotateDeg % 360) + 360) % 360;
      const need = (360 - targetCenter) % 360;
      let delta = need - base;
      if (delta <= 0) delta += 360;

      // nextTick 确保 transition 已挂载后再改角度，动画才会走
      this.$nextTick(() => {
        this.rotateDeg += SPIN_TURNS * 360 + delta;
      });

      clearTimeout(this._spinTimer);
      this._spinTimer = setTimeout(() => this.onSpinEnd(), SPIN_MS + 300);
    },
    onSpinEnd() {
      if (!this.spinning) return;
      this.spinning = false;
      const seg = this.segments[this._targetIdx];
      if (seg) this.sendSegment(seg);
    },
    sendSegment(seg) {
      const isBlast = seg.type === "blast";
      const url = isBlast ? "/gift/sendScreenBlash" : "/gift/sendGift";
      const param = isBlast
          ? { giftCode: seg.code, liveId: this.live.id }
          : { giftId: seg.id, liveId: this.live.id };

      this.sending = true;
      this.resultText = "选中「" + seg.name + "」，发送中…";

      this.$http.post(url, param).then((res) => {
        if (res.data.isSend) {
          this.$store.dispatch("live/getLiveInfo",this.live.id);
          this.resultText = "🎊 " + seg.name + " 已送出！";
          this.$Bus.$emit("refreshCoin");
          if (res.data.chatMsg) this.$Bus.$emit("appendChatMsg", res.data.chatMsg);
          // ★ 停留 1 秒 → 退出 → 重载并弹出新一轮
          clearTimeout(this._resultTimer);
          this._resultTimer = setTimeout(() => this.nextRound(), RESULT_STAY_MS);
        } else {
          this.resultText = "";
          this.$toast.fail(this.$i18n("insufficientCoins", "金币不足，请稍等..."));
          this.$Bus.$emit("triggerOpenRecharge");
        }
      }).catch(() => {
        this.resultText = "";
        this.$toast.fail(this.$i18n("sendFailed", "发送失败"));
      }).finally(() => {
        this.sending = false;
        // 等待期间状态被关闭：这轮结束后收起，且不再重开
        if (this._closePending) {
          this._closePending = false;
          this.show = false;
        }
      });
    },

    /** ★ 本轮结束：退出 → 重载（重新拉列表 + 随机选 6 个）→ 重新弹出 */
    nextRound() {
      this.show = false;
      clearTimeout(this._reopenTimer);
      this._reopenTimer = setTimeout(() => {
        if (this.live && this.live.isOpenWishLive) {
          this.open();
        }
      }, NEXT_ROUND_DELAY);
    },
  },
};
</script>

<style lang="less" scoped>
/* ═══ 遮罩 ═══ */
.luckyWheelMask {
  position: fixed;
  inset: 0;
  z-index: 2100; // 高于 GiftMenu 弹窗(2000)
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ═══ 面板 ═══ */
.wheelPanel {
  position: relative;
  width: calc(100vw - 48px);
  max-width: 360px;
  padding: 22px 16px 20px;
  box-sizing: border-box;
  border-radius: 18px;
  background: linear-gradient(170deg, #4a2c1a 0%, #3a2011 55%, #2c170c 100%);
  border: 1px solid #d9a24a;
  box-shadow: 0 0 0 2px rgba(217, 162, 74, 0.25), 0 18px 44px rgba(0, 0, 0, 0.65);
  overflow: hidden;
  text-align: center;

  .closeBt {
    position: absolute;
    top: 8px;
    right: 10px;
    font-style: normal;
    font-size: 20px;
    color: rgba(255, 220, 160, 0.6);
    padding: 2px 8px;
    cursor: pointer;
    z-index: 3;
  }

  /* 光斑 */
  .glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(26px);
    pointer-events: none;
    &.glowA {
      width: 130px; height: 130px; top: -44px; left: -34px;
      background: rgba(255, 196, 100, 0.2);
      animation: glowDrift 6s ease-in-out infinite;
    }
    &.glowB {
      width: 96px; height: 96px; bottom: -34px; right: -24px;
      background: rgba(255, 120, 80, 0.16);
      animation: glowDrift 7s ease-in-out infinite reverse;
    }
  }

  .wheelTitle {
    font-size: 19px;
    font-weight: bold;
    letter-spacing: 3px;
    color: #ffe8c2;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  }
  .wheelSub {
    margin-top: 4px;
    font-size: 11px;
    letter-spacing: 2px;
    color: rgba(255, 215, 150, 0.6);
  }
}

/* ═══ 转盘区 ═══ */
.wheelWrap {
  position: relative;
  margin: 18px auto 0;
}
.wheelPointer {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 11px solid transparent;
  border-right: 11px solid transparent;
  border-top: 20px solid #ff4d4f;
  z-index: 3;
  filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.35));
}
.wheelCanvas {
  display: block;
  will-change: transform;
  // transition 改为行内 style 动态挂载（spinning 时才有），保证每轮归零瞬时完成
}
.spinBt {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 78px;
  height: 78px;
  border-radius: 50%;
  border: 4px solid #fff;
  background: linear-gradient(180deg, #ffe2a0 0%, #f5b94e 45%, #e69f2e 100%);
  color: #5a3a10;
  font-size: 19px;
  font-weight: bold;
  letter-spacing: 3px;
  box-shadow: 0 4px 14px rgba(230, 159, 46, 0.55), inset 0 1px 1px rgba(255, 255, 255, 0.85);
  cursor: pointer;
  z-index: 2;
  transition: transform 0.12s ease;
  &:active { transform: translate(-50%, -50%) scale(0.94); }
  &:disabled { opacity: 0.65; cursor: default; }
  .spinDots { letter-spacing: 2px; }
}

/* ═══ 结果文案 ═══ */
.wheelResult {
  margin-top: 14px;
  font-size: 14px;
  font-weight: bold;
  color: #ffd166;
  letter-spacing: 1px;
}

/* ═══ 过渡 ═══ */
.wheelFade-enter-active, .wheelFade-leave-active { transition: opacity 0.25s ease; }
.wheelFade-enter, .wheelFade-leave-to { opacity: 0; }

@keyframes glowDrift {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(10px, 8px) scale(1.12); }
}
</style>
